import React, { useEffect } from 'react';

import { useAppDispatch, useAppSelector } from '../../components/hooks';
import { convertFromDesignToLayoutCoordinates } from '../../library/layout-geometry/coordinates';
import { getSnapVisualTypeForSnappedMousePosition } from '../../library/layout-snapping/point-snapping/snapMousePosition';
import {
  beginPickedPointMovement,
  handlePickedPointInLayoutMode,
  PICKED_POINT_MOVEMENT_MODIFIER,
} from '../../store/actions/layout/layoutPickingActions';
import { getPickingObjectInfo, isOffsetPointPickingAllowed } from '../../store/selectors/pickingSelectors';
import { SnapVisual } from '../dimension/SnapVisual';
import { threeJsDom } from '../util/events/ThreeJSDom';
import { GlobalThreeJSDomObjectEvent } from '../util/events/types';
import { usePointPickingSnapContext } from './PointPickingSnapContext';

export function PointPickingVisualsAndEventListeners() {
  const snappingContext = usePointPickingSnapContext()!;
  const dispatch = useAppDispatch();
  const pickingObjectInfo = useAppSelector(getPickingObjectInfo);

  const canMovePickedPoint = useAppSelector(isOffsetPointPickingAllowed);

  useEffect(() => {
    if (pickingObjectInfo?.type !== 'pickingLayoutPoint') return;

    function onSceneClick(event: GlobalThreeJSDomObjectEvent) {
      const clickPosition2D = convertFromDesignToLayoutCoordinates(event.eventPosition.worldPosition).projectTo2D();
      const snapResult = snappingContext.snap(clickPosition2D);

      if (pickingObjectInfo?.type !== 'pickingLayoutPoint') return;

      if (canMovePickedPoint && event.metadata[PICKED_POINT_MOVEMENT_MODIFIER]) {
        dispatch(
          beginPickedPointMovement({ type: 'layoutPointPicking', target: pickingObjectInfo.target }, snapResult, {
            clientPosition: event.eventPosition.clientPosition,
            modifiers: event.metadata,
          }),
        );
      } else {
        dispatch(handlePickedPointInLayoutMode(snapResult.point.toObject(), { modifiers: event.metadata }));
      }
    }

    threeJsDom.addGlobalListener('click', onSceneClick);
    return () => void threeJsDom.removeGlobalListener('click', onSceneClick);
  });

  const { snapResult, sourcePoint } = snappingContext;
  if (snapResult == null || sourcePoint == null) return null;

  const snapVisualType = getSnapVisualTypeForSnappedMousePosition(snapResult);
  return snapVisualType ? <SnapVisual snapType={snapVisualType} position={snapResult.point.to3D().toArray()} /> : null;
}
